import { ObjectiveType } from "../types/client-education-detail.types";
import type {
  EducationDetailDto,
  ObjectiveDto,
  VirtualClassroomDto,
} from "../types/client-education-detail.types";

const DEFAULT_JITSI_DOMAIN = "https://meet.jit.si";

function normalize(url: string): string {
  // Strip trailing slash
  return url.replace(/\/$/, "");
}

export function getJitsiDomain(): string {
  const fromEnv = import.meta.env.VITE_JITSI_DOMAIN;
  if (typeof fromEnv === "string" && fromEnv.trim()) {
    return normalize(fromEnv.trim());
  }
  return DEFAULT_JITSI_DOMAIN;
}

export function buildJitsiRoomUrl(roomName: string): string {
  const domain = getJitsiDomain();
  return `${domain}/${encodeURIComponent(roomName)}`;
}

// Public meet.jit.si / 8x8.vc backend'in ürettiği token'ı (`iss` boş) reddeder;
// bu domain'lerde JWT eklenmez. Aynı liste JitsiEmbed içinde de var.
const PUBLIC_JITSI_DOMAINS = new Set(["meet.jit.si", "8x8.vc"]);

/**
 * İframe'de gösterilen ile aynı kimlikli (JWT'li) katılım URL'ini üretir.
 * "Canlı — Katıl" butonu JWT'siz açtığında self-hosted Jitsi odası kullanıcı
 * adı/şifre soruyor; token'ı `?jwt=` query param olarak ekleyerek bunu önleriz.
 */
export function buildAuthenticatedJitsiUrl(
  externalUrl: string,
  token?: string | null,
): string {
  if (!token) return externalUrl;
  try {
    const u = new URL(externalUrl);
    if (PUBLIC_JITSI_DOMAINS.has(u.host)) return externalUrl;
    u.searchParams.set("jwt", token);
    return u.toString();
  } catch {
    return externalUrl;
  }
}

/**
 * Eğitimin chapter'ları içinde JWT'li (token + externalUrl dolu) ilk Jitsi
 * objective'i. Sınıfın kendi URL/token çifti yoksa katılım bununla yapılır.
 */
export function findJitsiObjective(
  education: EducationDetailDto | null,
): ObjectiveDto | null {
  for (const chapter of education?.chapters ?? []) {
    for (const objective of chapter.objectives ?? []) {
      if (objective.type === ObjectiveType.JITSI && objective.externalUrl) {
        return objective;
      }
    }
  }
  return null;
}

/**
 * Sanal sınıfın Jitsi katılım URL'i ve token'ı.
 *
 * URL ve token AYNI sınıftan gelmelidir: Jitsi JWT'si `room` claim'ine bağlıdır;
 * eğitim seviyesindeki JITSI objective'i tek odaya işaret ettiği için ikinci
 * sınıfa tıklayan kullanıcı yanlış odaya düşüyor ya da token reddediliyordu.
 * Önce sınıfın kendi `jitsiUrl`/`jitsiToken` çifti, yoksa objective kullanılır.
 */
export function resolveClassroomJoinTarget(
  education: EducationDetailDto,
  vc: VirtualClassroomDto,
): { url: string; token: string | null } | null {
  const jitsiObjective = findJitsiObjective(education);
  const classroomUrl =
    vc.jitsiUrl ??
    (vc.jitsiRoomName ? buildJitsiRoomUrl(vc.jitsiRoomName) : null);
  const url = classroomUrl ?? jitsiObjective?.externalUrl ?? null;
  if (!url) return null;
  const token = classroomUrl
    ? (vc.jitsiToken ?? jitsiObjective?.token ?? null)
    : (jitsiObjective?.token ?? null);
  return { url, token };
}

/** Portalın canlı sınıf sayfası (Jitsi gömülü + giriş-çıkış kaydı + sınıf belgeleri). */
export function buildLiveClassroomPath(
  educationId: string,
  classroomId: string,
): string {
  return `/dashboard/live-classroom/${classroomId}?educationId=${encodeURIComponent(educationId)}`;
}

/** Jitsi oda URL'inin oda adı. */
export function getJitsiRoomName(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const room = new URL(url).pathname.replace(/^\//, "").split("/")[0];
    return room ? decodeURIComponent(room) : null;
  } catch {
    return null;
  }
}

/** Jitsi URL'inin odasına ait sanal sınıf (içerikteki Jitsi objective'i için). */
export function findClassroomForJitsiUrl(
  classrooms: VirtualClassroomDto[] | null | undefined,
  url: string | null | undefined,
): VirtualClassroomDto | null {
  const room = getJitsiRoomName(url);
  if (!room) return null;
  return (
    (classrooms ?? []).find(
      (vc) =>
        (vc.jitsiUrl != null && getJitsiRoomName(vc.jitsiUrl) === room) ||
        (vc.jitsiRoomName != null &&
          vc.jitsiRoomName.trim().replace(/\s+/g, "-") === room),
    ) ?? null
  );
}
